import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Input, Select, DatePicker, List, Avatar, Typography, Popconfirm, message, Empty, Spin, Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined, LeftOutlined, RightOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import useAuthStore from '../store/authStore';
import { getBranches } from '../api/branches';
import { getDailyNotes, createDailyNote, deleteDailyNote } from '../api/dailyNotes';

const { TextArea } = Input;
const { Text } = Typography;

const MANAGER_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];
const SUPER_ROLES = ['developer_admin', 'super_admin'];

const DailyNotes = () => {
  const { user } = useAuthStore();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const isSuperRole = SUPER_ROLES.includes(user?.role);

  const [notes, setNotes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [branchFilter, setBranchFilter] = useState(null);
  const [content, setContent] = useState('');
  const [noteBranch, setNoteBranch] = useState(null);

  useEffect(() => {
    if (isSuperRole) {
      getBranches().then(res => {
        const list = res.data?.data || res.data || [];
        setBranches(list);
        if (!noteBranch) setNoteBranch(list[0]?.id ?? null);
        if (!branchFilter) setBranchFilter(list[0]?.id ?? null);
      }).catch(() => {});
    } else if (user?.branch_id) {
      setNoteBranch(user.branch_id);
      setBranchFilter(user.branch_id);
    }
  }, []);

  const fetchNotes = useCallback(async () => {
    if (!branchFilter && !user?.branch_id) return;
    setLoading(true);
    try {
      const res = await getDailyNotes({
        date: selectedDate.format('YYYY-MM-DD'),
        branch_id: branchFilter || user?.branch_id || undefined,
      });
      setNotes(res.data || []);
    } catch {
      message.error('Notlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, branchFilter, user?.branch_id]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleSubmit = async () => {
    if (!content.trim()) return message.warning('Not içeriği boş olamaz.');
    if (!noteBranch) return message.warning('Şube seçin.');
    setSubmitting(true);
    try {
      await createDailyNote({
        content: content.trim(),
        branch_id: noteBranch,
        date: selectedDate.format('YYYY-MM-DD'),
      });
      setContent('');
      fetchNotes();
      message.success('Not eklendi.');
    } catch {
      message.error('Not eklenemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDailyNote(id);
      setNotes(prev => prev.filter(n => n.id !== id));
      message.success('Not silindi.');
    } catch {
      message.error('Not silinemedi.');
    }
  };

  const canDelete = (note) => {
    if (MANAGER_ROLES.includes(user?.role)) return true;
    return note.user_id === user?.id;
  };

  const goDay = (offset) => setSelectedDate(prev => prev.add(offset, 'day'));
  const isToday   = selectedDate.isSame(dayjs(), 'day');
  const isPastDay = selectedDate.isBefore(dayjs(), 'day');
  const isFutureDay = selectedDate.isAfter(dayjs(), 'day');

  return (
    <div>
      {/* Başlık */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold mb-0">Günlük Notlar</h2>
        <div className="flex items-center gap-2">
          {isSuperRole && (
            <Select
              style={{ width: 160 }}
              placeholder="Şube"
              value={branchFilter}
              onChange={setBranchFilter}
              options={branches.map(b => ({ label: b.name, value: b.id }))}
            />
          )}
          {/* Geçmiş günler serbestçe gezilebilir — sadece GÖRÜNTÜLEME için.
              Not ekleme/silme geçmiş günlerde kapalı (aşağıya bakınız).
              Eskiden sol ok isPastDay iken kilitleniyordu, yani ancak bir gün
              geriye gidilebiliyordu; tarih seçici de geçmişi tamamen
              kapatıyordu. "Şu gün ne girilmişti?" sorusu cevapsız kalıyordu. */}
          <Button icon={<LeftOutlined />} onClick={() => goDay(-1)} />
          <DatePicker
            value={selectedDate}
            onChange={(d) => d && setSelectedDate(d)}
            format="DD MMMM YYYY"
            allowClear={false}
            style={{ width: 170 }}
          />
          <Button icon={<RightOutlined />} onClick={() => goDay(1)} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Sol — Not ekleme */}
        <div className="col-span-1">
          <Card
            title={<span className="text-base font-semibold">Yeni Not</span>}
            size="small"
          >
            <div className="flex flex-col gap-3">
              {isSuperRole && branches.length > 1 && (
                <Select
                  placeholder="Şube seç"
                  value={noteBranch}
                  onChange={setNoteBranch}
                  style={{ width: '100%' }}
                  options={branches.map(b => ({ label: b.name, value: b.id }))}
                />
              )}
              <TextArea
                rows={6}
                placeholder="Notunuzu buraya yazın..."
                value={content}
                onChange={e => setContent(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSubmit(); }}
                maxLength={1000}
                showCount
              />
              <Text type="secondary" style={{ fontSize: 11 }}>Ctrl + Enter ile hızlıca ekle</Text>
              {isFutureDay && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Bu not {selectedDate.format('DD MMMM YYYY')} tarihinde görünecek.
                </Text>
              )}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleSubmit}
                loading={submitting}
                disabled={isPastDay}
                block
              >
                {isPastDay ? 'Geçmiş tarihe not eklenemez' : 'Not Ekle'}
              </Button>
            </div>
          </Card>
        </div>

        {/* Sağ — Not listesi */}
        <div className="col-span-2">
          <Card
            title={
              <div className="flex items-center gap-2">
                <FileTextOutlined />
                <span>{selectedDate.format('DD MMMM YYYY')} Notları</span>
                {notes.length > 0 && (
                  <Tag color="blue">{notes.length} not</Tag>
                )}
              </div>
            }
            size="small"
          >
            {loading ? (
              <div className="flex justify-center py-12"><Spin /></div>
            ) : notes.length === 0 ? (
              <Empty description="Bu tarihte henüz not yok." className="py-8" />
            ) : (
              <List
                dataSource={notes}
                renderItem={(note) => (
                  <List.Item
                    key={note.id}
                    actions={
                      // Geçmiş gün salt-okunur: silme de kapalı. Geçmiş günler
                      // yeni açıldığı için bu bir yetki kaybı değil — daha önce
                      // o günlere hiç ulaşılamıyordu.
                      canDelete(note) && !isPastDay
                        ? [
                            <Popconfirm
                              key="del"
                              title="Bu notu silmek istediğinizden emin misiniz?"
                              onConfirm={() => handleDelete(note.id)}
                              okText="Sil"
                              cancelText="İptal"
                              okButtonProps={{ danger: true }}
                            >
                              <Button danger type="text" size="small" icon={<DeleteOutlined />} />
                            </Popconfirm>,
                          ]
                        : []
                    }
                  >
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1677ff' }} />}
                      title={
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">
                            {note.user ? `${note.user.name} ${note.user.surname}` : 'Bilinmeyen'}
                          </span>
                          {note.branch && (
                            <Tag color="blue" style={{ fontSize: 11 }}>{note.branch.name}</Tag>
                          )}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(note.created_at).format('DD MMM · HH:mm')}
                          </Text>
                        </div>
                      }
                      description={
                        <div style={{ whiteSpace: 'pre-wrap', color: '#374151' }}>
                          {note.content}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DailyNotes;
